import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useApi, useList } from '@/hooks/useApi';
import { permissionSignature, isOrgAdmin } from '@/lib/permissions';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PageLoading } from '@/components/ui/loading';
import { PlusCircle, RefreshCw } from 'lucide-react';
import { ANALYSIS_WIDGET_KEYS, DASHBOARD_WIDGET_BY_KEY, resolveAuthorizedWidgetKeys } from '@shared/dashboard-contract';
import type { DashboardActionPayload, DashboardAnalysis, DashboardStats, Project } from '@/lib/types';
import { StatGrid, StatGridFour, StatGridSix, SectionEmpty, SectionHeader, type StatCardDef } from '@/components/dashboard/shared';
import { renderWidget, widgetVariant } from '@/components/dashboard/registry';
import * as W from '@/components/dashboard/widgets';

const ACTION_LINKS: Record<string, string> = {
  new_requirement: '/requirements/new',
  new_actual: '/actuals/new',
};

// Build the drill-down URLs used by widgets. For the non-admin dashboard no
// date/project filters exist; for the admin Organization Analysis section they
// mirror the analysis filter bar so charts and lists drill into filtered pages.
function buildDashboardUrls(projectId = '', startDate = '', endDate = '') {
  const pid = (explicit?: string) => explicit || projectId || '';
  const reqUrl = (opts: Record<string, string> = {}) => {
    const p = new URLSearchParams();
    if (opts.status) p.set('status', opts.status);
    if (opts.skillLevel) p.set('skillLevel', opts.skillLevel);
    const selected = pid(opts.projectId);
    if (selected) p.set('projectId', selected);
    if (startDate) p.set('dateFrom', startDate);
    if (endDate) p.set('dateTo', endDate);
    const qs = p.toString();
    return qs ? `/requirements?${qs}` : '/requirements';
  };
  const actualsUrl = (opts: Record<string, string> = {}) => {
    const p = new URLSearchParams();
    if (opts.status) p.set('status', opts.status);
    if (opts.labourType) p.set('labourType', opts.labourType);
    if (opts.skillLevel) p.set('skillLevel', opts.skillLevel);
    const selected = pid(opts.projectId);
    if (selected) p.set('projectId', selected);
    if (startDate) p.set('dateFrom', startDate);
    if (endDate) p.set('dateTo', endDate);
    const qs = p.toString();
    return qs ? `/actuals?${qs}` : '/actuals';
  };
  return { reqUrl, actualsUrl, filters: { projectId, startDate, endDate } };
}

export default function DashboardPage() {
  const { user } = useAuth();
  const permissions = user?.permissions ?? [];
  const admin = isOrgAdmin(user);
  const permSig = permissionSignature(permissions);
  const userId = user?.id ?? 'anonymous';
  const authorizedKeys = new Set(resolveAuthorizedWidgetKeys(permissions, admin));

  const { data: stats, isLoading, isError, error, refetch } = useApi<DashboardStats>(
    ['dashboard', 'stats', userId, permSig, String(admin)],
    '/dashboard/stats'
  );

  if (isLoading) return <PageLoading />;

  if (admin) {
    return <AdminDashboard userId={userId} permSig={permSig} stats={stats} authorizedKeys={authorizedKeys} />;
  }
  return <ScopedDashboard stats={stats} isError={isError} error={error} refetch={refetch} />;
}

// ── ORG_ADMIN: organization overview + Organization Analysis ───────────────
function AdminDashboard({
  userId,
  permSig,
  stats,
  authorizedKeys,
}: {
  userId: string;
  permSig: string;
  stats: DashboardStats | undefined;
  authorizedKeys: Set<string>;
}) {
  const { user } = useAuth();
  const [projectId, setProjectId] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Optional date range: empty means "all-time". start-only = on/after,
  // end-only = on/before, both = inclusive. Only start > end is invalid.
  const datesInvalid = startDate.length === 10 && endDate.length === 10 && startDate > endDate;
  const isResetDisabled = projectId === 'all' && !startDate && !endDate;
  const resetFilters = () => {
    setProjectId('all');
    setStartDate('');
    setEndDate('');
  };

  const params = new URLSearchParams();
  if (projectId !== 'all') params.set('projectId', projectId);
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  const queryString = params.toString();

  const { data: projects } = useList<Project>(['dashboard', 'projects', userId, permSig, 'true'], '/dashboard/projects');

  const { data: analysis, isError, error, refetch } = useApi<DashboardAnalysis>(
    ['dashboard', 'analysis', userId, permSig, projectId, startDate, endDate],
    `/dashboard/analysis${queryString ? `?${queryString}` : ''}`,
    !datesInvalid
  );

  const summaryCards = (stats?.widgets ?? [])
    .filter(w => authorizedKeys.has(w.key) && widgetVariant(w.key) === 'stat');
  const summaryUrls = buildDashboardUrls();

  const analysisWidgets = (analysis?.widgets ?? []).filter(w => ANALYSIS_WIDGET_KEYS.includes(w.key as (typeof ANALYSIS_WIDGET_KEYS)[number]));
  const analysisUrls = buildDashboardUrls(projectId === 'all' ? '' : projectId, startDate, endDate);
  const chartKeys = new Set(['requirements_by_status', 'planned_vs_actual', 'regular_vs_adhoc', 'labour_by_skill', 'cost_by_project']);
  const recentKeys = new Set(['recent_approved_requirements', 'recent_approved_actuals']);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-heading font-bold text-primary">
          Welcome, {user?.name?.split(' ')[0]}
        </h1>
        <p className="text-secondary mt-1">Organization overview</p>
      </div>

      {summaryCards.length > 0 ? (
        <StatGrid cards={summaryCards.map(({ key, data }) => renderWidget(key, data, summaryUrls, true) as StatCardDef)} />
      ) : (
        <SectionEmpty message="No organization summary is available." />
      )}

      <section className="space-y-4">
        <SectionHeader title="Organization Analysis" />
        <Card>
          <CardContent className="p-4">
            <div className="grid cw-grid-4 gap-3">
              <div className="min-w-0">
                <Label htmlFor="analysis-project">Project</Label>
                <Select value={projectId} onValueChange={setProjectId}>
                  <SelectTrigger id="analysis-project" aria-label="Filter analysis by project"><SelectValue placeholder="All Projects" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Projects</SelectItem>
                    {projects?.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-0">
                <Label htmlFor="analysis-start">Start Date</Label>
                <DatePicker id="analysis-start" value={startDate} onChange={e => setStartDate(e.target.value)} />
              </div>
              <div className="min-w-0">
                <Label htmlFor="analysis-end">End Date</Label>
                <DatePicker id="analysis-end" value={endDate} onChange={e => setEndDate(e.target.value)} />
              </div>
              <div className="flex min-w-0 items-end">
                <Button
                  variant="outline"
                  size="default"
                  onClick={resetFilters}
                  disabled={isResetDisabled}
                  className="w-full"
                >
                  <RefreshCw size={14} className="mr-2" /> Reset
                </Button>
              </div>
            </div>
            {datesInvalid && (
              <p className="text-error text-sm mt-2">Start date cannot be after end date.</p>
            )}
            {isError && (
              <div className="mt-3 flex items-center justify-between gap-3 rounded-lg bg-error/10 p-3 text-sm text-error">
                <span>Could not load analysis data{error ? `: ${(error as any).message || 'Please try again.'}` : ''}</span>
                <Button variant="outline" size="sm" onClick={() => refetch()}>Retry</Button>
              </div>
            )}
          </CardContent>
        </Card>

        {analysis && analysisWidgets.length > 0 ? (
          <>
            {analysisWidgets.filter(w => chartKeys.has(w.key)).length > 0 && (
              <div className="grid cw-grid-charts gap-6">
                {analysisWidgets.filter(w => chartKeys.has(w.key)).map(({ key, data }) => (
                  <div key={key}>{renderWidget(key, data, analysisUrls, true) as React.ReactNode}</div>
                ))}
              </div>
            )}
            {analysisWidgets.filter(w => recentKeys.has(w.key)).length > 0 && (
              <div className="grid cw-grid-charts gap-6">
                {analysisWidgets.filter(w => recentKeys.has(w.key)).map(({ key, data }) => (
                  <div key={key}>{renderWidget(key, data, analysisUrls, true) as React.ReactNode}</div>
                ))}
              </div>
            )}
          </>
        ) : analysis && !isError ? (
          <SectionEmpty message="No analysis data for the selected range." />
        ) : null}
      </section>
    </div>
  );
}

// ── All other roles: flat profile contract ─────────────────────────────────
// Layout is driven entirely by the worker's `profile` payload (derived from
// effective permissions), never by role names:
//   - Site Engineer: exactly six owner-scoped cards in a 6-column grid
//   - Manager / Cluster Head (and equivalent custom roles): exactly four
//     approval cards in a 4-column grid
//   - read-only roles: informational approved cards plus one compact
//     recently-approved section
// There is no queue panel and no separate recent-decisions section anymore.
// Widgets present in the response that are not part of the profile are never
// rendered, and all cards stay visible at zero (no collapsing).
function ScopedDashboard({
  stats,
  isError,
  error,
  refetch,
}: {
  stats: DashboardStats | undefined;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}) {
  const { user } = useAuth();
  const urls = buildDashboardUrls();

  const profile = stats?.profile;
  const widgetsByKey = new Map((stats?.widgets ?? []).map(w => [w.key, w.data]));

  const cardInfos = (profile?.cards ?? [])
    .filter(key => widgetsByKey.has(key))
    .map(key => ({
      key,
      def: renderWidget(key, widgetsByKey.get(key), urls, false) as StatCardDef,
    }));

  const recentKey = profile?.recent?.key;
  const recentData = recentKey && widgetsByKey.has(recentKey) ? widgetsByKey.get(recentKey) : undefined;
  const recentItems = (recentData as any)?.items ?? [];

  const noAccess = !profile || (profile.cards.length === 0 && !profile.queue && !profile.recent);
  const emptyText = (key: string) => DASHBOARD_WIDGET_BY_KEY[key]?.emptyText ?? 'No records in this range.';
  const subtitle = 'Your work at a glance';

  // Pick the grid by card count: six-card Site Engineer profile, four-card
  // approval profile, otherwise the generic responsive grid. 4/2/1 and 6/3/1
  // column behavior on desktop/tablet/mobile - never a horizontal scroll.
  const cardGrid = (cards: StatCardDef[]) =>
    cards.length >= 6 ? <StatGridSix cards={cards} /> : cards.length === 4 ? <StatGridFour cards={cards} /> : <StatGrid cards={cards} />;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold text-primary">
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-secondary mt-1">{subtitle}</p>
        </div>
        {stats?.actions.length ? (
          <div className="flex flex-wrap gap-2">
            {stats.actions.map((action: DashboardActionPayload) => {
              const to = ACTION_LINKS[action.key];
              return to ? (
                <Link key={action.key} to={to}>
                  <Button variant="default" size="sm">
                    <PlusCircle size={14} className="mr-1.5" /> {action.label}
                  </Button>
                </Link>
              ) : null;
            })}
          </div>
        ) : null}
      </div>

      {isError ? (
        <div className="flex items-center justify-between gap-3 rounded-lg bg-error/10 p-3 text-sm text-error">
          <span>Unable to load dashboard data{error ? `: ${(error as any).message || 'Please try again.'}` : '.'}</span>
          <Button variant="outline" size="sm" onClick={() => refetch()}>Retry</Button>
        </div>
      ) : stats ? (
        noAccess ? (
          <SectionEmpty message="No dashboard widgets are available for your access level." />
        ) : (
          <div className="space-y-6">
            {cardInfos.length > 0 && cardGrid(cardInfos.map(c => c.def))}
            {!!recentData && recentKey && recentItems.length > 0 && (
              <W.RecentListCard
                title={profile?.recent?.title ?? ''}
                emptyText={emptyText(recentKey)}
                items={recentItems}
              />
            )}
          </div>
        )
      ) : (
        <SectionEmpty message="No dashboard data is available." />
      )}
    </div>
  );
}
